
import { kypV3ApiService, CategoryStatus, CategoryData } from '../../services/kypV3ApiService';
import { KYPPipelineType } from '../types/kyp-search.types';

interface CategoryPollingCallbacks {
  onStatusUpdate: (statuses: CategoryStatus) => void;
  onNewCategoryCompleted: (categoryData: CategoryData[]) => void;
  onAllCategoriesCompleted: (allData: CategoryData[]) => void;
  onError: (error: string) => void;
  onPollingComplete: () => void;
}

export class CategoryPollingManager {
  private pollingRef: number | null = null;
  private pollingTimeoutRef: number | null = null;
  private isStopped = false;
  private searchId: string;
  private lastStatusCount: number = 0;
  private readonly POLLING_INTERVAL = 2000; // 2 seconds
  private readonly MAX_POLLING_TIME = 900000; // 15 minutes
  private callbacks: CategoryPollingCallbacks;
  private token: string;

  private pipelineType: KYPPipelineType;

  constructor(searchId: string, token: string, pipelineType: KYPPipelineType, callbacks: CategoryPollingCallbacks) {
    this.searchId = searchId;
    this.token = token;
    this.pipelineType = pipelineType;
    this.callbacks = callbacks;
  }

  public startPolling() {
    this.isStopped = false;

    this.pollingTimeoutRef = window.setTimeout(() => {
      this.callbacks.onError('Polling timed out after 15 minutes.');
      this.stopPolling();
    }, this.MAX_POLLING_TIME);

    this.pollingRef = window.setInterval(() => {
      this.checkCategoryStatuses();
    }, this.POLLING_INTERVAL);

    this.checkCategoryStatuses(); // Initial check
  }

  public stopPolling() {
    this.isStopped = true;
    if (this.pollingRef) {
      window.clearInterval(this.pollingRef);
      this.pollingRef = null;
    }
    if (this.pollingTimeoutRef) {
      window.clearTimeout(this.pollingTimeoutRef);
      this.pollingTimeoutRef = null;
    }
    this.callbacks.onPollingComplete();
  }

  private isCancellationError(error: any): boolean {
    return error.name === 'AbortError' || error.code === 'ERR_CANCELED';
  }

  private async checkCategoryStatuses() {
    console.log('[CategoryPollingManager] checkCategoryStatuses called');
    if (this.isStopped) {
      console.log('[CategoryPollingManager] Polling is stopped, returning');
      return;
    }
    const abortController = new AbortController();

    try {
      console.log('[CategoryPollingManager] Calling getCategoryStatuses...');
      const getStatuses = this.pipelineType === 'parallel_categories_mock'
        ? kypV3ApiService.getMockCategoryStatuses
        : kypV3ApiService.getCategoryStatuses;

      const statuses = await getStatuses(this.searchId, this.token, abortController.signal);
      if (this.isStopped) return;

      console.log('[CategoryPollingManager] Statuses received:', statuses);
      this.callbacks.onStatusUpdate(statuses);

      const completedCount = Object.values(statuses).filter(Boolean).length;
      console.log(`[CategoryPollingManager] Completed count: ${completedCount}, Last status count: ${this.lastStatusCount}`);

      if (completedCount > this.lastStatusCount) {
        this.lastStatusCount = completedCount;
        await this.fetchNewCompletedCategories();
      }

      const totalCategories = Object.keys(statuses).length;
      if (completedCount === totalCategories && totalCategories > 0) {
        console.log('[CategoryPollingManager] All categories completed, stopping polling.');
        this.stopPolling();
      }
    } catch (error) {
      console.error('[CategoryPollingManager] Error in checkCategoryStatuses:', error);
      if (!this.isCancellationError(error)) {
        const message = error.response?.data?.detail || 'Failed to check category statuses.';
        this.callbacks.onError(message);
        this.stopPolling();
      }
    }
  }

  private async fetchNewCompletedCategories() {
    if (this.isStopped) return;
    const abortController = new AbortController();

    try {
      const getCompleted = this.pipelineType === 'parallel_categories_mock'
        ? kypV3ApiService.getMockCompletedCategories
        : kypV3ApiService.getCompletedCategories;

      const categories = await getCompleted(this.searchId, this.token, abortController.signal);
      if (this.isStopped) return;

      console.log('[CategoryPollingManager] Fetched completed categories:', categories);
      this.callbacks.onNewCategoryCompleted(categories);

      const getStatuses = this.pipelineType === 'parallel_categories_mock'
        ? kypV3ApiService.getMockCategoryStatuses
        : kypV3ApiService.getCategoryStatuses;

      const statuses = await getStatuses(this.searchId, this.token, abortController.signal);
      const totalCategories = Object.keys(statuses).length;
      const completedCount = Object.values(statuses).filter(Boolean).length;
      
      console.log('[CategoryPollingManager] Status check - completed:', completedCount, 'total:', totalCategories);
      
      if (completedCount === totalCategories && totalCategories > 0) {
        console.log('[CategoryPollingManager] All categories completed, calling onAllCategoriesCompleted');
        this.callbacks.onAllCategoriesCompleted(categories);
        this.stopPolling();
      }

    } catch (error) {
      console.error('[CategoryPollingManager] Error fetching completed categories:', error);
      if (!this.isCancellationError(error)) {
        this.callbacks.onError('Failed to fetch completed categories.');
        this.stopPolling();
      }
    }
  }
}
